import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from '@headlessui/react';
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { Fragment, useState } from 'react';
import { useFormContext } from 'react-hook-form';

export type LimitedMultiSelectOption = {
  id: string | number;
  label: string;
  value: string;
  time?: string;
};

const generateTimelineOptions = (
  startTime = '09:00',
  endTime = '17:00',
  interval = 15,
): LimitedMultiSelectOption[] => {
  const options: LimitedMultiSelectOption[] = [];
  const start = new Date(`2000-01-01T${startTime}`);
  const end = new Date(`2000-01-01T${endTime}`);

  for (
    let current = new Date(start);
    current <= end;
    current.setMinutes(current.getMinutes() + interval)
  ) {
    const timeString = current.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const value = current.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    options.push({
      id: value,
      label: timeString,
      value,
      time: current.toISOString(),
    });
  }

  return options;
};

interface LimitedMultiSelectProps {
  label?: string;
  error?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  name: string;
  options?: LimitedMultiSelectOption[];
  value?: LimitedMultiSelectOption[];
  onChange?: (value: LimitedMultiSelectOption[]) => void;
  limit?: number;
  placeholder?: string;
  startTime?: string;
  endTime?: string;
  timeInterval?: number;
}

export function LimitedMultiSelect({
  label,
  error: propError,
  className,
  required,
  disabled,
  name,
  options: propOptions,
  value: propValue,
  onChange: propOnChange,
  limit = 2,
  placeholder = 'Select up to 2 options...',
  startTime,
  endTime,
  timeInterval = 15,
}: LimitedMultiSelectProps): JSX.Element {
  const form = useFormContext();
  const error = propError || (form?.formState?.errors[name]?.message as string);
  const [query, setQuery] = useState('');
  const [internalValue, setInternalValue] = useState<
    LimitedMultiSelectOption[]
  >([]);

  const options =
    propOptions || generateTimelineOptions(startTime, endTime, timeInterval);

  const filteredOptions = options
    .filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase()),
    )
    .filter((option) => {
      // Triple-check all possible sources of selected values
      const formValue = form?.watch(name) as
        | LimitedMultiSelectOption[]
        | undefined;
      const isInFormValue = formValue?.some(
        (selected) => selected.id === option.id,
      );
      const isInPropValue = propValue?.some(
        (selected) => selected.id === option.id,
      );
      const isInInternalValue = internalValue?.some(
        (selected) => selected.id === option.id,
      );

      return !(isInFormValue || isInPropValue || isInInternalValue);
    });

  // Get value from form context or props or internal state
  const value = form
    ? (form.watch(name) as LimitedMultiSelectOption[])
    : propValue ?? internalValue;
  const defaultPlaceholder = `Select up to ${limit} option${
    limit === 1 ? '' : 's'
  }...`;
  const finalPlaceholder = placeholder ?? defaultPlaceholder;
  const handleChange = (newValue: LimitedMultiSelectOption[]): void => {
    // Limit selections based on the limit prop
    const limitedValue =
      newValue.length > limit ? newValue.slice(-limit) : newValue;

    if (form) {
      form.setValue(name, limitedValue, { shouldValidate: true });
    } else {
      setInternalValue(limitedValue);
    }
    propOnChange?.(limitedValue);
  };

  const removeOption = (optionToRemove: LimitedMultiSelectOption): void => {
    const newValue = value.filter((v) => v.id !== optionToRemove.id);
    handleChange(newValue);
  };

  return (
    <div className={clsx('w-full space-y-1', className)}>
      <Combobox
        value={value || []}
        onChange={handleChange}
        multiple
        disabled={disabled}
      >
        {({ open }) => (
          <>
            {label && (
              <ComboboxLabel className="flex-1 font-nunito text-sm font-normal leading-6 text-[#434343]">
                {label}
                {required && <span className="text-[#CE3F4F] ml-1">*</span>}
              </ComboboxLabel>
            )}
            <div className="relative mt-1">
              <div
                className={clsx(
                  'relative w-full cursor-default overflow-hidden rounded-[6px] transition-all duration-300 h-10',
                  disabled
                    ? 'bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] border-[1px] border-[var(--Border-border-gray,#D9D9D9)]'
                    : 'border border-[#D9D9D9] bg-[#FEFDFC]',
                  error && 'border-red-300',
                )}
              >
                <div className="flex flex-wrap gap-1 p-1">
                  {value?.map((item) => (
                    <span
                      key={item.id}
                      className={clsx(
                        'inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm transition-colors duration-300 h-full',
                        disabled
                          ? 'bg-[var(--Color-Neutral-Fill-colorFillTertiary,rgba(0,0,0,0.04))] text-[var(--Text-text-disabled,#8C8C8C)]'
                          : 'bg-gray-100 text-gray-900',
                      )}
                    >
                      {item.label}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            removeOption(item);
                          }}
                          className="rounded-sm hover:bg-gray-200"
                        >
                          <XMarkIcon className="h-4 w-4 text-gray-900" />
                        </button>
                      )}
                    </span>
                  ))}
                  <ComboboxButton className="flex-1">
                    <ComboboxInput
                      className={clsx(
                        'w-full border-none py-2 pl-3 pr-10',
                        'font-roboto text-base leading-6 text-[#141414]',
                        'focus:outline-none',
                      )}
                      onChange={(event) => setQuery(event.target.value)}
                      placeholder={value?.length === 0 ? finalPlaceholder : ''}
                    />
                  </ComboboxButton>
                </div>
                <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDownIcon
                    className={clsx(
                      'h-5 w-5 transition-transform duration-200',
                      disabled ? 'text-[#8C8C8C]' : 'text-gray-900',
                      open && 'rotate-180',
                    )}
                    aria-hidden="true"
                  />
                </ComboboxButton>
              </div>
              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setQuery('')}
              >
                <ComboboxOptions
                  className={clsx(
                    'absolute z-10 mt-1 max-h-60 w-full overflow-auto',
                    'rounded-md bg-white py-1 shadow-lg',
                    'ring-1 ring-black ring-opacity-5',
                    'custom-scrollbar',
                  )}
                >
                  {filteredOptions.length === 0 && query !== '' ? (
                    <div className="relative cursor-default select-none px-4 py-2 text-gray-700">
                      Nothing found.
                    </div>
                  ) : (
                    filteredOptions.map((option) => (
                      <ComboboxOption
                        key={option.id}
                        value={option}
                        className={clsx(
                          'relative cursor-pointer select-none py-2 pl-10 pr-4',
                          'text-gray-900 hover:bg-gray-100',
                        )}
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={clsx(
                                'block truncate',
                                selected ? 'font-medium' : 'font-normal',
                              )}
                            >
                              {option.label}
                            </span>
                            {selected && (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                ✓
                              </span>
                            )}
                          </>
                        )}
                      </ComboboxOption>
                    ))
                  )}
                </ComboboxOptions>
              </Transition>
            </div>
          </>
        )}
      </Combobox>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
}
